<template>
    <div class="d-flex flex-grow-1 flex-column">
        <router-link
            class="d-flex flex-column entry-card flex-grow-1"
            @click.native="handleCardClick(caseObj)"
            :to="communityDetailRouteObjConditional(caseObj)"
        >
            <!-- Card Cover Image -->
            <div
                class="community-text-body scenario-title no-decor flex-grow-0"
                tabindex="0"
            >
                <img
                    v-if="caseObj.cover_image"
                    :src="caseObj.cover_image.signed_img_urls.sm"
                    class="image-cover flex-grow-0"
                    :style="{ height: coverHeightPx + 'px' }"
                    :alt="caseObj.cover_image_alt_text"
                    loading="lazy"
                />
            </div>
            <!-- Card Body -->
            <div class="d-flex flex-column flex-grow-1 entry-card-body">
                <!-- Contributor List -->
                <div class="flex-grow-0">
                    <div class="entry-card-contributors">
                        <div class="d-flex align-items-center" :id="_uid">
                            <div class="flex-grow-1 d-flex align-items-center">
                                <div
                                    class="flex-grow-0 mr-2"
                                    v-for="contributor in caseObj.contributors"
                                    :key="contributor.id"
                                >
                                    <info-popover :content="contributor.publisher.name" placement="top">
                                        <router-link
                                            :to="publisherDetailRouteObj(contributor.publisher)"
                                            class="flex-grow-0 logo-container logo-sm d-flex align-items-center rounded-circle"
                                            :aria-label="'Explore contributor ' + contributor.publisher.name"
                                            @click.stop.prevent
                                        >
                                            <img
                                                v-if="contributor.publisher.logo_file"
                                                @click="handlePublisherClick($event, contributor.publisher)"
                                                :src="contributor.publisher.logo_file.signed_img_urls.thumb"
                                                class="logo-circle"
                                                :alt="'View ' + contributor.publisher.name"
                                            />
                                        </router-link>
                                    </info-popover>
                                </div>
                            </div>
                            <div
                                class="flex-grow-0 ml-2 mr-2 d-flex align-items-center tiny"
                                v-if="addedToLibrary && addedToOrg"
                            >
                                <span
                                    class="icon icon-100 align-middle text-success icon-glyphicons-basic-circle-check mr-1"
                                ></span>
                                <span class="text-secondary align-middle">
                                    Added to
                                    <span class="font-weight-bold">{{ addedToOrg.name }}</span>
                                </span>
                            </div>
                            <div class="flex-grow-0 d-flex align-items-center mr-2" v-if="showAddActions">
                                <button
                                    class="btn btn-icon py-0 px-0 entry-card-hover-btn"
                                    @click.stop.prevent="openAddModal()"
                                >
                                    <span class="icon icon-100 align-middle icon-glyphicons-basic-plus"></span>
                                </button>
                            </div>
                            <div class="flex-grow-0 d-flex align-items-center">
                                <div class="mr-1" v-if="showPreviewLink">
                                    <a
                                        class="entry-card-hover-btn small"
                                        target="__blank"
                                        :href="communityDetailRouteHref(caseObj)"
                                        @click.stop
                                    >
                                        <span class="mr-1">Preview</span
                                        ><span
                                            class="icon icon-100 align-middle icon-glyphicons-basic-square-new-window mr-1"
                                        ></span
                                    ></a>
                                </div>
                                <div class="dropdown" ref="dropdown">
                                    <a
                                        class="btn btn-icon px-0 py-0 entry-card-hover-btn"
                                        role="button"
                                        aria-expanded="false"
                                        @click.stop.prevent="toggleDropdown()"
                                        ref="dropdownToggle"
                                    >
                                        <span
                                            aria-hidden="true"
                                            class="icon icon-lg align-middle icon-glyphicons-basic-more-vertical"
                                        ></span>
                                    </a>
                                    <div class="dropdown-menu dropdown-menu-right shadow-sm medium" ref="dropdownMenu">
                                        <a class="dropdown-item py-2" v-if="showAddActions" @click.stop.prevent="openAddModal()">
                                            <span class="icon icon-100 align-middle icon-glyphicons-basic-plus mr-1"></span>
                                            <span class="align-middle">Clone to library</span>
                                        </a>
                                        <a class="dropdown-item py-2" target="_blank" @click.stop :href="caseUrl">
                                            <span
                                                class="icon icon-100 align-middle icon-glyphicons-basic-square-new-window mr-1"
                                            ></span>
                                            <span class="align-middle">Open in a new tab</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Description & Metadata -->
                <div
                    class="no-decor entry-card-metadata flex-grow-1 d-flex flex-column"
                    tabindex="0"
                >
                    <div class="flex-grow-0 entry-card-description-text community-text-body scenario-title">
                        <div class="d-flex" style="margin-bottom: 8px">
                            {{ caseObj.community_title }}
                        </div>
                        <div>
                            <p class="small text-secondary scenario-description">
                                {{ caseObj.community_description }}
                            </p>
                        </div>
                    </div>
                    <div class="flex-grow-1 d-flex flex-column justify-content-end">
                        <div class="d-flex align-items-center entry-card-tags">
                            <div class="flex-grow-1">
                                <div class="community-text-sm">
                                    <span
                                        class="icon icon-100 icon-glyphicons-basic-tags card-icon-mr"
                                        style="margin-left: 1px"
                                    ></span>
                                    <a
                                        @click.stop="handleCategoryClick($event, tag.category)"
                                        :href="categoryUrl(tag.category)"
                                        class="mb-0 font-weight-500 category-link"
                                        v-for="(tag, index) in visibleCategoryTags"
                                        :key="tag.id"
                                    >
                                        {{ tag.category.name }}
                                        <span v-if="index < visibleCategoryTags.length - 1">&middot;</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1 d-flex align-items-center">
                                <div class="text-secondary font-weight-500 community-text-xs mr-3">
                                    <span class="icon-lg icon-glyphicons-basic-clock align-middle card-icon-mr"></span>
                                    <span class="align-middle">{{ caseObj.duration }} {{ caseObj.duration_units }}</span>
                                </div>
                            </div>
                            <div class="flex-grow-0 ml-2" v-if="caseObj.community_is_featured">
                                <div class="d-flex align-items-center">
                                    <span class="badge badge-success-highlight badge-tiny align-middle">Featured</span>
                                </div>
                            </div>
                            <div class="flex-grow-0 ml-2" v-else-if="caseObj.community_is_new">
                                <div class="d-flex align-items-center">
                                    <span class="badge badge-highlight badge-tiny align-middle">New</span>
                                </div>
                            </div>
                            <div class="flex-grow-0" v-else>
                                <div class="text-secondary font-weight-500 community-text-xs">
                                    <span class="icon-lg icon-glyphicons-basic-trending align-middle card-icon-mr"></span>
                                    <span class="align-middle">{{ formatViewCount(caseObj.view_count) }} Views</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </router-link>
        <community-add-modal
            :case-obj="caseObj"
            v-on:added-to-library="markAsAdded($event)"
            ref="communityAddModal"
        ></community-add-modal>
    </div>
</template>

<script>
import Utilities from "@/assets/js/utilities.js"
import InfoPopover from "@/components/InfoPopover"
import CommunityAddModal from "@/components/CommunityAddModal"

export default {
    name: "CommunityCaseCard",
    components: {
        InfoPopover,
        CommunityAddModal,
    },
    props: {
        caseObj: {
            type: Object,
            required: true,
        },
        // When false, the 'add to library' buttons are hidden.
        showAddActions: {
            type: Boolean,
            required: false,
            default: true,
        },
        // When true, all nav links are opened in separate tabs, rather than in the
        // same browser window.
        openNavLinksInTabs: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Controls the height of cover images shown in each card.
        coverHeightPx: {
            type: Number,
            default() {
                return 200
            },
            required: false,
        },
        // When true, a link to open the detail view in a new tab will be shown over
        // the card when hovering over it.
        showPreviewLink: {
            type: Boolean,
            default: false,
            required: false,
        },
    },
    data() {
        return {
            addedToLibrary: false,
            addedToOrg: null,
            dropdownIsOpen: false,
        }
    },
    computed: {
        caseUrl() {
            return "/community/browse/" + this.caseObj.id
        },
        visibleCategoryTags() {
            return this.caseObj.community_tags.filter((t) => t.category.is_visible)
        },
    },
    methods: {
        publisherDetailRouteObj(publisher) {
            return {
                name: "CommunityPublisherDetail",
                params: {
                    id: publisher.id,
                },
            }
        },
        communityDetailRouteObjConditional(caseObj) {
            if (this.openNavLinksInTabs) {
                return {}
            }
            return this.communityDetailRouteObj(caseObj)
        },
        communityDetailRouteObj(caseObj) {
            return { name: "CommunityCaseDetail", params: { id: caseObj.id } }
        },
        communityDetailRouteHref(caseObj) {
            let route = this.communityDetailRouteObj(caseObj)
            return this.$router.resolve(route).href
        },
        handlePublisherClick(event, publisher) {
            if (this.openNavLinksInTabs) {
                // Open the community detail view in another tab.
                event.preventDefault()
                let route = this.publisherDetailRouteObj(publisher)
                window.open(this.$router.resolve(route).href, "_blank")
            }
        },
        handleCategoryClick(event, category) {
            if (this.openNavLinksInTabs) {
                // Open the community category in another tab.
                event.preventDefault()
                let route = this.categoryUrl(category)
                window.open(this.$router.resolve(route).href, "_blank")
            }
        },
        handleCardClick(caseObj) {
            this.$emit("item-selected", caseObj)
        },
        formatViewCount(count) {
            return Utilities.formatViewCount(count)
        },
        categoryUrl(category) {
            return `/community/browse/?tag=${category.name}`
        },
        openAddModal() {
            this.$refs.communityAddModal.open()
            this.closeDropdown()
        },
        markAsAdded(organization) {
            this.addedToLibrary = true
            this.addedToOrg = organization
        },
        openDropdown() {
            this.$refs.dropdown.classList.add('show')
            this.$refs.dropdownMenu.classList.add('show')
            this.$refs.dropdownToggle.setAttribute('aria-expanded', 'true')
            this.dropdownIsOpen = true
        },
        closeDropdown() {
            this.$refs.dropdown.classList.remove('show')
            this.$refs.dropdownMenu.classList.remove('show')
            this.$refs.dropdownToggle.setAttribute('aria-expanded', 'false')
            this.dropdownIsOpen = false
        },
        // Toggle the open/close state of the dropdown menu.
        toggleDropdown() {
            const isShown = this.$refs.dropdownMenu.classList.contains('show')
            isShown ? this.closeDropdown() : this.openDropdown()
        },
        // Listen for outside clicks to close the dropdown.
        handleClickOutside(event) {
            // Check if click was outside the dropdown element.
            const dropdown = this.$refs.dropdown
            if (this.dropdownIsOpen && dropdown && !dropdown.contains(event.target)) {
                this.closeDropdown()
            }
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/community.scss";

.scenario-title {
    margin-bottom: 6px;
    font-weight: 500;
    color: $body-color !important;
}

.scenario-description {
    margin-bottom: 8px;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
}

.image-cover {
    width: 100%;
    object-fit: cover;
    border-top-left-radius: $border-radius-lg;
    border-top-right-radius: $border-radius-lg;
}

.entry-card {
    background-color: #ffffff;
    border-radius: $border-radius-lg;
    box-shadow: $box-shadow-sm;

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            .entry-card-hover-btn {
                opacity: 1;
            }
        }
    }
}

.entry-card-hover-btn {
    transition: $transition-base;
    &:not(:focus) {
        opacity: 0;
    }
}

.entry-card-body {
    padding: 14px;
}

.entry-card-metadata {
    color: $body-color !important;
}

.entry-card-description-text {
    margin-bottom: 14px;
}

.entry-card-contributors {
    margin-bottom: 12px;
}

.entry-card-tags {
    margin-bottom: 12px;
}

.card-icon-mr {
    margin-right: 3px;
}

.badge-tiny {
    padding: 0.32em 0.5em !important;
    font-size: 70%;
    margin-top: 1px;
}
</style>
